import { RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import Button from '@mui/material/Button';
import BreadcrumbLayout from 'components/layout/BreadcrumbLayout';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import {
  newPasswordValidationRules,
  oldPasswordValidationRules,
  passwordMatchRules,
} from '@insights-ltd/design-library/utils';
import {
  InputType,
  InputText,
  TextV2,
  FullScreenSpinner,
  FullScreenError,
  SectionTitle,
  SvgLock2Bold,
} from '@insights-ltd/design-library/components';
import ValidationTags from 'components/ValidationTags';
import { useChangePassword, useGetSignInType } from 'api';
import { useQueryClient } from '@tanstack/react-query';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { Box, styled } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';

type FormData = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const StyledForm = styled('form')(({ theme }) => ({
  '> * + *': {
    marginTop: theme.spacing(spacingSizeMap.L),
  },
}));

const ChangePassword = () => {
  const { user } = useAuth();
  const { status: signInTypeStatus, data: signInTypeData } = useGetSignInType(
    user?.emailAddress as string,
  );

  const [queryParameters] = useSearchParams();
  const dialect = queryParameters.get('dialect') ?? 'en-GB';
  const queryClient = useQueryClient();
  const {
    mutate,
    isPending,
    error: changePasswordError,
  } = useChangePassword(queryClient);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    mode: 'onChange',
    criteriaMode: 'all',
  });
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onSubmit = ({ oldPassword, newPassword }: FormData) =>
    mutate(
      {
        dialect,
        oldPassword,
        newPassword,
      },
      { onSuccess: () => navigate('/account') },
    );

  if (signInTypeStatus === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (signInTypeStatus === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.my-account.error.load')}
      />
    );
  }

  if (signInTypeData.method === 'EXTERNAL') {
    return <Navigate to="/account" />;
  }

  return (
    <BreadcrumbLayout
      titleKey="ui.event-management.title.change-password"
      activeTextKey="ui.event-management.my-account.change-password"
      crumbs={{
        '/': t('ui.event-management.dashboard.home.title'),
        '/account': t('ui.event-management.my-account.title'),
      }}
      headingKey="ui.event-management.my-account.change-password"
    >
      <Box
        sx={{
          mt: spacingSizeMap.L,
          mb: spacingSizeMap.L,
        }}
      >
        <SectionTitle
          icon={{ icon: <SvgLock2Bold />, iconColour: 'green' }}
          text={t('ui.event-management.my-account.my-password')}
        />
      </Box>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Grid
          container
          spacing={2}
          sx={{
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <InputText
              id="oldPassword"
              type={InputType.PASSWORD}
              fullWidth
              label={t(
                'ui.event-management.my-account.old-password.input-label',
              )}
              error={
                Boolean(changePasswordError) || Boolean(errors.oldPassword)
              }
              {...register('oldPassword', oldPasswordValidationRules)}
              placeholder={t(
                'ui.event-management.my-account.old-password.input-placeholder',
              )}
              helperText={
                changePasswordError &&
                t('ui.event-management.my-account.old-password.error')
              }
            />
          </Grid>
          <Grid size={{ md: 12 }}>
            <TextV2 variant="bodyBold" color="textSecondary">
              {t('ui.event-management.my-account.change-password.instruction')}
            </TextV2>
          </Grid>
          <Grid>
            <ValidationTags
              value={watch('newPassword', '')}
              confirmationValue={watch('confirmPassword', '')}
              errors={errors.newPassword}
              confirmErrors={errors.confirmPassword}
            />
          </Grid>
          <Grid>
            <InputText
              id="newPassword"
              type={InputType.PASSWORD}
              fullWidth
              label={t(
                'ui.event-management.my-account.new-password.input-label',
              )}
              error={Boolean(errors.newPassword)}
              {...register(
                'newPassword',
                newPasswordValidationRules(getValues) as RegisterOptions & {
                  deps?: undefined;
                },
              )}
              placeholder={t(
                'ui.event-management.my-account.new-password.input-placeholder',
              )}
            />
          </Grid>
          <Grid>
            <InputText
              id="confirmPassword"
              type={InputType.PASSWORD}
              fullWidth
              label={t(
                'ui.event-management.my-account.confirm-password.input-label',
              )}
              error={Boolean(errors.confirmPassword)}
              {...register(
                'confirmPassword',
                passwordMatchRules(getValues) as RegisterOptions & {
                  deps?: undefined;
                },
              )}
              placeholder={t(
                'ui.event-management.my-account.confirm-password.input-label',
              )}
            />
          </Grid>
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
              mt: -1,
              mr: -1,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              disabled={isPending}
              endIcon={
                isPending && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                )
              }
            >
              {t('ui.event-management.my-account.change-password')}
            </Button>
          </Grid>
        </Grid>
      </StyledForm>
    </BreadcrumbLayout>
  );
};

export default ChangePassword;
